import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DashboardLayoutContainer } from 'components/Layouts/DashboardLayout';
import { Box } from '@mui/material';
import {
  IconGraphy,
  Typography,
  colors,
  Badge,
  Loading,
  Table,
  Button,
  Dropdown,
} from '@leapeasy/ui-kit';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import { openDetails } from 'store/actions/uiActions';
import { getDelegationDetailAction, updateUserPropertyAction } from 'store/actions/userActions';
import styled from 'styled-components';
import { GlobalUXState } from 'components/GlobalUXState';
import { failedNotif } from 'store/actions/notifActions';
import { buildMaskedUserBuildingRow, maskDemoField } from 'utils/demoMask';
import { DetailPageShell } from 'components/DetailPageState';
import { useEntityDetailPageState } from 'hooks/useDetailPageState';
import { useImmutableJs } from 'hooks/useImmutableJs';
import { useBuildingListFetch } from 'hooks/useBuildingListFetch';
import { fitContentTextColumnOptions } from 'utils/fitContentTextColumnOptions';
import { BOOLEAN_YES_NO_OPTIONS, formatBooleanYesNo } from 'utils/formatBooleanYesNo';
import { useInvitePropertyPerUserFetch } from 'hooks/useInvitePropertyPerUserFetch';
import { useDropFilterActiveSummaryBar } from 'hooks/useDropFilterActiveSummaryBar';
import {
  buildPropertyListFilterChips,
  parsePropertyListFilterChipId,
} from 'utils/propertyListActiveFilterChips';

const EMPTY_PROPERTY_FILTER = {
  options: {},
  searchText: '',
  searchPlaceholder: 'Search Property..',
};

const PROPERTY_TYPE_FILTER_OPTIONS = [
  {
    text: 'Guarantor Waiver+LDR',
    value: 'Event Process;Auto Enroll',
  },
  {
    text: 'Rent Guaranty',
    value: 'Auto Enroll',
  },
];

export const UserEdit = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { delegationId } = useParams();

  const [tableData, setTableData] = useState([]);
  const [propertyTableData, setPropertyTableData] = useState([]);
  const [delegatedBuildings, setDelegatedBuildings] = useState([]);
  const [myBuildings, setMyBuildings] = useState([]);
  const [disable, setDisable] = useState(false);
  // Keep add/remove diffs across paginated pages until Save/Discard
  const pendingAddedRef = useRef(new Map());
  const pendingRemovedRef = useRef(new Map());
  const handledPropertySaveSuccessRef = useRef(false);
  const [pendingChangesVersion, setPendingChangesVersion] = useState(0);

  // Local filters per table — avoid shared URL search state so Delegated and My
  // Properties searches/filters don't clobber each other.
  const [filter, setFilter] = useState(EMPTY_PROPERTY_FILTER);
  const [selectedBType, selSelectedBType] = useState([]);
  const [isSHouse, setIsSHouse] = useState([]);

  // Delegated Building Sort & delegatedBuildingPagination
  const [sortDelegatedBuildingOptions, setDelegatedBuildingSortOptions] = useState({});
  const [delegatedBuildingPagination, setDelegatedBuildingPagination] = useState({
    pageNumber: 0,
    rowsPerPage: 12,
    maxVisiblePageItems: 6,
    onUpdate: (value) => {
      if (value === -1) value = 0;
      setDelegatedBuildingPagination((prev) => ({ ...prev, pageNumber: value }));
    },
    showPageNumberInput: 6,
  });
  // My Building Sort & Pagniation
  const [sortMyBuildingOptions, setMyBuildingSortOptions] = useState({});
  const [myBuildingPagination, setMyBuildingPagination] = useState({
    pageNumber: 0,
    rowsPerPage: 12,
    maxVisiblePageItems: 6,
    onUpdate: (value) => {
      if (value === -1) value = 0;
      setMyBuildingPagination((prev) => ({ ...prev, pageNumber: value }));
    },
    showPageNumberInput: 6,
  });
  const [myPropertiesFilter, setMyPropertiesFilter] = useState(EMPTY_PROPERTY_FILTER);
  const [mySelectedBType, setMySelectedBType] = useState([]);
  const [myIsSHouse, setMyIsSHouse] = useState([]);

  const buildings = useSelector((state) => state.getIn(['property', 'building']));
  const propertyBuildings = useSelector((state) => state.getIn(['invite', 'inviteProperty']));

  const buildingsJSON = buildings ? buildings.toJS() : null;
  const propertyBuildingsJSON = propertyBuildings ? propertyBuildings.toJS() : null;

  const isDemo = useSelector((state) => state.getIn(['ui', 'demo']));
  const details = useSelector((state) => state.getIn(['ui', 'details'])) || null;
  const delegationDetail =
    useSelector((state) => state.getIn(['user', 'delegation_detail'])) || null;
  const delegationDetailJSON = useImmutableJs(delegationDetail);
  const legacyDetailsType = details ? details.get('type') : null;
  const legacyFallbackJSON = useImmutableJs(details?.get('data') ?? null);
  const delegationDetailLoading = useSelector((state) =>
    state.getIn(['user', 'delegation_detail_loading']),
  );
  const delegationDetailError = useSelector((state) =>
    state.getIn(['user', 'delegation_detail_error']),
  );
  const hasUiFallback = !delegationId && legacyDetailsType === 'user' && details?.get('data');
  const {
    data: activeDetail,
    isLoading: detailLoading,
    hasError: detailHasError,
  } = useEntityDetailPageState({
    entityId: delegationId,
    entityData: delegationDetailJSON ?? legacyFallbackJSON,
    isLoading: delegationDetailLoading,
    hasError: delegationDetailError,
    hasUiFallback,
  });
  const detailReady = Boolean(activeDetail) && !detailLoading && !detailHasError;
  const pendingAddedCount = useMemo(() => pendingAddedRef.current.size, [pendingChangesVersion]);
  const pendingRemovedCount = useMemo(
    () => pendingRemovedRef.current.size,
    [pendingChangesVersion],
  );

  // Exclude pending removals and append pending adds so the server page window
  // stays 12 rows (adds land on later pages instead of overflowing the current page).
  const delegatedPropertyIdsForFetch = useMemo(() => {
    const source = Array.isArray(activeDetail?.property) ? activeDetail.property : [];
    const ids = source.map(String).filter((id) => !pendingRemovedRef.current.has(id));
    const seen = new Set(ids);
    for (const id of pendingAddedRef.current.keys()) {
      if (!seen.has(id) && !pendingRemovedRef.current.has(id)) {
        ids.push(id);
        seen.add(id);
      }
    }
    return ids;
  }, [activeDetail?.property, pendingRemovedCount, pendingAddedCount]);

  useBuildingListFetch({
    propertyId: delegatedPropertyIdsForFetch,
    filter,
    pagination: delegatedBuildingPagination,
    sortOptions: sortDelegatedBuildingOptions,
    selectedBType,
    isSHouse,
    enabled: detailReady,
  });

  useInvitePropertyPerUserFetch(activeDetail, {
    enabled: detailReady,
    pagination: myBuildingPagination,
    sortOptions: sortMyBuildingOptions,
    filter: myPropertiesFilter,
    selectedBType: mySelectedBType,
    isSHouse: myIsSHouse,
  });

  useEffect(() => {
    setDelegatedBuildingPagination((prev) =>
      prev.pageNumber === 0 ? prev : { ...prev, pageNumber: 0 },
    );
  }, [filter.searchText, selectedBType, isSHouse]);

  useEffect(() => {
    setMyBuildingPagination((prev) => (prev.pageNumber === 0 ? prev : { ...prev, pageNumber: 0 }));
  }, [myPropertiesFilter.searchText, mySelectedBType, myIsSHouse]);

  // Clamp page when removals shrink the list below the current offset.
  useEffect(() => {
    const rows = delegatedBuildingPagination.rowsPerPage || 12;
    const total = Math.max(0, delegatedPropertyIdsForFetch.length);
    const maxPage = Math.max(0, Math.ceil(total / rows) - 1);
    if (delegatedBuildingPagination.pageNumber > maxPage) {
      setDelegatedBuildingPagination((prev) => ({ ...prev, pageNumber: maxPage }));
    }
  }, [
    delegatedPropertyIdsForFetch.length,
    delegatedBuildingPagination.pageNumber,
    delegatedBuildingPagination.rowsPerPage,
  ]);

  // Clamp My Properties when pending add/remove diffs change the virtual total.
  useEffect(() => {
    const rows = myBuildingPagination.rowsPerPage || 12;
    const serverTotal = Math.max(0, propertyBuildingsJSON?.total ?? 0);
    const total = Math.max(0, serverTotal - pendingAddedCount + pendingRemovedCount);
    const maxPage = Math.max(0, Math.ceil(total / rows) - 1);
    if (myBuildingPagination.pageNumber > maxPage) {
      setMyBuildingPagination((prev) => ({ ...prev, pageNumber: maxPage }));
    }
  }, [
    propertyBuildingsJSON?.total,
    pendingAddedCount,
    pendingRemovedCount,
    myBuildingPagination.pageNumber,
    myBuildingPagination.rowsPerPage,
  ]);

  const onClickBack = useCallback(() => {
    navigate(-1);
    // dispatch(openDetails(null));
  }, [navigate]);

  const goDetailPage = (index) => {
    const selectedBuilding = buildingsJSON?.data?.[index];
    const buildingId = selectedBuilding?.building_id;
    if (selectedBuilding && buildingId) {
      navigate(`/property/properties/${buildingId}/detail/report`);
      dispatch(
        openDetails({
          type: 'property',
          data: selectedBuilding,
        }),
      );
      return;
    }
    dispatch(failedNotif('Property detail ID is missing.'));
  };
  useEffect(() => {
    const pageData = Array.isArray(buildingsJSON?.data) ? buildingsJSON.data : [];
    // Removals are already excluded from the fetch query; keep a defensive filter.
    // Do not append pending adds here — they are included in delegatedPropertyIdsForFetch
    // so the server page stays ≤ rowsPerPage and adds appear on later pages via total/offset.
    const withoutRemoved = pageData.filter(
      (building) => !pendingRemovedRef.current.has(String(building.building_id)),
    );
    setDelegatedBuildings(withoutRemoved);
  }, [buildings, pendingChangesVersion]);

  // Keep the current server page ≤ rowsPerPage and park pending
  // removes at the end of the list so they show on later pages.
  useEffect(() => {
    const pageData = Array.isArray(propertyBuildingsJSON?.data) ? propertyBuildingsJSON.data : [];
    const withoutAdded = pageData.filter(
      (building) => !pendingAddedRef.current.has(String(building.building_id)),
    );
    const pendingRemoves = [...pendingRemovedRef.current.values()].filter(
      (building) =>
        !withoutAdded.some((item) => String(item.building_id) === String(building.building_id)),
    );

    const serverTotal = Math.max(0, propertyBuildingsJSON?.total ?? pageData.length);
    const serverVisibleTotal = Math.max(0, serverTotal - pendingAddedRef.current.size);
    const rowsPerPage = myBuildingPagination.rowsPerPage || 12;
    const offset = myBuildingPagination.pageNumber * rowsPerPage;
    const pendingStart = Math.max(0, offset - serverVisibleTotal);
    const pendingEnd = Math.max(0, offset + rowsPerPage - serverVisibleTotal);
    const pendingSlice =
      pendingEnd > pendingStart ? pendingRemoves.slice(pendingStart, pendingEnd) : [];

    setMyBuildings([...withoutAdded, ...pendingSlice]);
  }, [
    propertyBuildings,
    pendingChangesVersion,
    myBuildingPagination.pageNumber,
    myBuildingPagination.rowsPerPage,
  ]);

  useEffect(() => {
    const data = delegatedBuildings.map((building) =>
      buildMaskedUserBuildingRow(building, isDemo, building.building_id),
    );
    setTableData(data);
  }, [delegatedBuildings, isDemo]);

  useEffect(() => {
    const data = myBuildings.map((building) =>
      buildMaskedUserBuildingRow(building, isDemo, building.building_id),
    );
    setPropertyTableData(data);
  }, [myBuildings, isDemo]);

  useEffect(() => {
    if (delegationId) {
      dispatch(getDelegationDetailAction(delegationId));
      return;
    }
    const fallbackDetails = details ? details.toJS() : null;
    if (!details || fallbackDetails.type !== 'user') {
      onClickBack();
      return;
    }
  }, [onClickBack, details, delegationId, dispatch]);

  const propertyUpdateStatus = useSelector((state) =>
    state.getIn(['user', 'property_update_status']),
  );

  // Clear pending diffs only after a successful save AND refreshed delegation
  // detail, so the list never briefly re-fetches the pre-save property set.
  useEffect(() => {
    if (propertyUpdateStatus === 'pending' || propertyUpdateStatus === 'failure') {
      handledPropertySaveSuccessRef.current = false;
    }
    if (
      propertyUpdateStatus !== 'success' ||
      delegationDetailLoading ||
      handledPropertySaveSuccessRef.current
    ) {
      return;
    }
    handledPropertySaveSuccessRef.current = true;
    pendingAddedRef.current.clear();
    pendingRemovedRef.current.clear();
    setPendingChangesVersion((version) => version + 1);
    setDisable(false);
    setDelegatedBuildingPagination((prev) =>
      prev.pageNumber === 0 ? prev : { ...prev, pageNumber: 0 },
    );
  }, [propertyUpdateStatus, delegationDetailLoading, activeDetail?.property]);

  const user = activeDetail;
  const onRetry = useCallback(() => {
    if (delegationId) dispatch(getDelegationDetailAction(delegationId));
  }, [delegationId, dispatch]);
  const delegatedBuildingCount = delegatedBuildings.length;
  // Pending adds/removals are already in delegatedPropertyIdsForFetch. Prefer that
  // length when not searching so the footer updates before the next fetch returns.
  const delegatedPropertiesTotal = Math.max(
    0,
    filter?.searchText?.trim()
      ? (buildingsJSON?.total ?? 0)
      : Math.max(buildingsJSON?.total ?? 0, delegatedPropertyIdsForFetch.length),
  );
  const myPropertiesData = Array.isArray(propertyBuildingsJSON?.data)
    ? propertyBuildingsJSON.data
    : [];
  const myPropertiesTotal = Math.max(
    0,
    (propertyBuildingsJSON?.total ?? myPropertiesData.length) -
      pendingAddedCount +
      pendingRemovedCount,
  );
  const shouldShowDelegatedLoading =
    Boolean(buildingsJSON?.isLoading) && delegatedBuildingCount > 0 && !disable;

  const delegatedBuildingColumns = [
    {
      name: 'Name',
      options: {
        ...fitContentTextColumnOptions(),
        sort: true,
        customBodyRenderer: (value, _) => (
          <CustomTypography align="left" variant="body2" style={{ cursor: 'pointer' }}>
            {value}
          </CustomTypography>
        ),
      },
      key: 'name',
    },
    {
      name: 'Property Type',
      options: {
        flex: '80px 1 1',
        customBodyRenderer: (value) => (
          <Badge
            background="rgba(243, 241, 244, 1)"
            color={value ? 'purple' : 'tomato'}
            label={
              !value
                ? 'undefined'
                : value === 'Auto Enroll'
                  ? 'AE'
                  : value === 'Event Process'
                    ? 'EP'
                    : 'AE EP'
            }
            rounded
            textSize="medium"
          />
        ),
        sort: true,
      },
      key: 'building_type',
    },
    {
      name: 'Phone Number',
      options: {
        sort: true,
        customBodyRenderer: (value, _) => (
          <CustomTypography align="left" variant="body2" style={{ cursor: 'pointer' }}>
            {value}
          </CustomTypography>
        ),
      },
      key: 'phone',
    },
    {
      name: 'Address',
      options: {
        flex: '170px 1 1',
        sort: true,
        customBodyRenderer: (value, _) => (
          <CustomTypography align="left" variant="body2" style={{ cursor: 'pointer' }}>
            {value}
          </CustomTypography>
        ),
      },
      key: 'billingStreet',
    },
    {
      name: 'Email Address',
      options: {
        flex: '120px 1 1',
        sort: true,
        customBodyRenderer: (value, _) => (
          <CustomTypography align="left" variant="body2" style={{ cursor: 'pointer' }}>
            {value}
          </CustomTypography>
        ),
      },
      key: 'email_address',
    },
    {
      name: 'Student Housing',
      options: {
        flex: '40px 1 1',
        filter: true,
        sort: true,
        customBodyRenderer: (value) => (
          <Badge
            background="#F3F1F4"
            color={value === 'true' ? 'parisGreen' : 'tomato'}
            label={formatBooleanYesNo(value)}
            rounded
            textSize="medium"
          />
        ),
        filterOptions: BOOLEAN_YES_NO_OPTIONS,
      },
      key: 'student_housing',
    },
    {
      name: 'Action',
      options: {
        flex: '30px 1 1',
        sort: false,
        customBodyRenderer: (value, tableMeta) => (
          <Button
            size="small"
            variant="tertiary"
            style={{ flexShrink: 2, color: '#702572' }}
            onClick={() => handleRemoveButton(value)}
          >
            Remove
          </Button>
        ),
      },
      key: 'detail',
    },
  ];
  const myBuildingColumns = [
    {
      name: 'Name',
      options: {
        ...fitContentTextColumnOptions(),
        sort: true,
        customBodyRenderer: (value, _) => (
          <CustomTypography align="left" variant="body2" style={{ cursor: 'pointer' }}>
            {value}
          </CustomTypography>
        ),
      },
      key: 'name',
    },
    {
      name: 'Property Type',
      options: {
        flex: '80px 1 1',
        customBodyRenderer: (value) => (
          <Badge
            background="rgba(243, 241, 244, 1)"
            color={value ? 'purple' : 'tomato'}
            label={
              !value
                ? 'undefined'
                : value === 'Auto Enroll'
                  ? 'AE'
                  : value === 'Event Process'
                    ? 'EP'
                    : 'AE EP'
            }
            rounded
            textSize="medium"
          />
        ),
        sort: true,
      },
      key: 'building_type',
    },
    {
      name: 'Phone Number',
      options: {
        sort: true,
        customBodyRenderer: (value, _) => (
          <CustomTypography align="left" variant="body2" style={{ cursor: 'pointer' }}>
            {value}
          </CustomTypography>
        ),
      },
      key: 'phone',
    },
    {
      name: 'Address',
      options: {
        flex: '170px 1 1',
        sort: true,
        customBodyRenderer: (value, _) => (
          <CustomTypography align="left" variant="body2" style={{ cursor: 'pointer' }}>
            {value}
          </CustomTypography>
        ),
      },
      key: 'billingStreet',
    },
    {
      name: 'Email Address',
      options: {
        flex: '120px 1 1',
        sort: true,
        customBodyRenderer: (value, _) => (
          <CustomTypography align="left" variant="body2" style={{ cursor: 'pointer' }}>
            {value}
          </CustomTypography>
        ),
      },
      key: 'email_address',
    },
    {
      name: 'Student Housing',
      options: {
        flex: '40px 1 1',
        filter: true,
        sort: true,
        customBodyRenderer: (value) => (
          <Badge
            background="#F3F1F4"
            color={value === 'true' ? 'parisGreen' : 'tomato'}
            label={formatBooleanYesNo(value)}
            rounded
            textSize="medium"
          />
        ),
        filterOptions: BOOLEAN_YES_NO_OPTIONS,
      },
      key: 'student_housing',
    },
    {
      name: 'Action',
      options: {
        flex: '5px 1 1',
        sort: false,
        customBodyRenderer: (value, tableMeta) => (
          <Button
            size="small"
            variant="tertiary"
            style={{ flexShrink: 2, color: '#702572' }}
            iconPrefix={'General.Add'}
            onClick={() => handleAddButton(value)}
          >
            Add
          </Button>
        ),
      },
      key: 'detail',
    },
  ];

  const delegatedRef = useRef(null);
  const myRef = useRef(null);

  // ui-kit cycles 1 → -1 → undefined; drop cleared keys so refetch keys stay stable.
  const normalizeTableSortOptions = useCallback((nextSort = {}) => {
    return Object.fromEntries(
      Object.entries(nextSort || {}).filter(([, direction]) => direction === 1 || direction === -1),
    );
  }, []);

  const handleDelegatedSortChange = useCallback(
    (nextSort) => {
      setDelegatedBuildingSortOptions(normalizeTableSortOptions(nextSort));
      setDelegatedBuildingPagination((prev) =>
        prev.pageNumber === 0 ? prev : { ...prev, pageNumber: 0 },
      );
    },
    [normalizeTableSortOptions],
  );

  const handleMyPropertiesSortChange = useCallback(
    (nextSort) => {
      setMyBuildingSortOptions(normalizeTableSortOptions(nextSort));
      setMyBuildingPagination((prev) =>
        prev.pageNumber === 0 ? prev : { ...prev, pageNumber: 0 },
      );
    },
    [normalizeTableSortOptions],
  );

  const handleAddButton = (buildingId) => {
    const id = String(buildingId);
    const selectedBuilding = myBuildings.find((item) => String(item.building_id) === id);
    if (!selectedBuilding) return;

    const restoredPendingRemoval = pendingRemovedRef.current.delete(id);
    if (!restoredPendingRemoval) {
      pendingAddedRef.current.set(id, selectedBuilding);
    }

    if (activeDetail?.accepter_role === 'pm') {
      const currentDelegated = delegatedBuildings[0];
      const currentId = currentDelegated?.building_id ? String(currentDelegated.building_id) : null;
      if (currentId && currentId !== id) {
        const removedPendingAddition = pendingAddedRef.current.delete(currentId);
        if (!removedPendingAddition) {
          pendingRemovedRef.current.set(currentId, currentDelegated);
        }
      }
      setDelegatedBuildings([selectedBuilding]);
      setMyBuildings((prev) => {
        const next = prev.filter((item) => String(item.building_id) !== id);
        return currentDelegated ? [...next, currentDelegated] : next;
      });
      setPendingChangesVersion((version) => version + 1);
      setDisable(true);
      return;
    }

    // Do not append onto the current page — pending adds are paged via fetch IDs.
    setMyBuildings((prev) => prev.filter((item) => String(item.building_id) !== id));
    setPendingChangesVersion((version) => version + 1);
    setDisable(true);
  };

  const handleRemoveButton = (buildingId) => {
    const id = String(buildingId);
    const selectedBuilding = delegatedBuildings.find((item) => String(item.building_id) === id);
    if (!selectedBuilding) return;

    const removedPendingAddition = pendingAddedRef.current.delete(id);
    if (!removedPendingAddition) {
      pendingRemovedRef.current.set(id, selectedBuilding);
    }

    // Do not append onto the current My Properties page — pending removes are
    // sliced onto later pages in the merge effect.
    setDelegatedBuildings((prev) => prev.filter((item) => String(item.building_id) !== id));
    setPendingChangesVersion((version) => version + 1);
    setDisable(true);
  };

  const handleDiscard = () => {
    pendingAddedRef.current.clear();
    pendingRemovedRef.current.clear();
    setDelegatedBuildings(Array.isArray(buildingsJSON?.data) ? buildingsJSON.data : []);
    setMyBuildings(myPropertiesData);
    setPendingChangesVersion((version) => version + 1);
    setDisable(false);
  };

  const handleSave = () => {
    if (!activeDetail) return;

    const addedIds = [...pendingAddedRef.current.keys()].map(String);
    const removedIds = [...pendingRemovedRef.current.keys()].map(String);

    if (addedIds.length === 0 && removedIds.length === 0) {
      setDisable(false);
      return;
    }

    // Keep pending diffs until UPDATE_USER_PROPERTY_SUCCESS so a failed save
    // does not lose the user's intended changes or refetch with a wrong set.
    dispatch(updateUserPropertyAction({ addedIds, removedIds, delegation: activeDetail }));
  };

  const resetDelegatedFilters = useCallback(() => {
    selSelectedBType([]);
    setIsSHouse([]);
  }, []);
  const applyDelegatedFilters = useCallback(() => {}, []);

  const resetMyFilters = useCallback(() => {
    setMySelectedBType([]);
    setMyIsSHouse([]);
  }, []);
  const applyMyFilters = useCallback(() => {}, []);

  const delegatedFilterChips = useMemo(
    () =>
      buildPropertyListFilterChips({
        selectedBType,
        isSHouse,
        unitFilter: null,
      }),
    [selectedBType, isSHouse],
  );
  const myFilterChips = useMemo(
    () =>
      buildPropertyListFilterChips({
        selectedBType: mySelectedBType,
        isSHouse: myIsSHouse,
        unitFilter: null,
      }),
    [mySelectedBType, myIsSHouse],
  );

  const handleRemoveDelegatedFilterChip = useCallback((chipId) => {
    const parsed = parsePropertyListFilterChipId(chipId);
    if (!parsed) return;
    if (parsed.type === 'propertyType') {
      selSelectedBType((prev) => prev.filter((s) => s !== parsed.value));
      return;
    }
    if (parsed.type === 'studentHousing') {
      setIsSHouse((prev) => prev.filter((s) => s !== parsed.value));
    }
  }, []);

  const handleRemoveMyFilterChip = useCallback((chipId) => {
    const parsed = parsePropertyListFilterChipId(chipId);
    if (!parsed) return;
    if (parsed.type === 'propertyType') {
      setMySelectedBType((prev) => prev.filter((s) => s !== parsed.value));
      return;
    }
    if (parsed.type === 'studentHousing') {
      setMyIsSHouse((prev) => prev.filter((s) => s !== parsed.value));
    }
  }, []);

  const {
    dropFilterActiveSummary: delegatedDropFilterActiveSummary,
    dropFilterActiveCount: delegatedDropFilterActiveCount,
  } = useDropFilterActiveSummaryBar(
    delegatedFilterChips,
    handleRemoveDelegatedFilterChip,
    resetDelegatedFilters,
  );
  const {
    dropFilterActiveSummary: myDropFilterActiveSummary,
    dropFilterActiveCount: myDropFilterActiveCount,
  } = useDropFilterActiveSummaryBar(myFilterChips, handleRemoveMyFilterChip, resetMyFilters);

  const delegatedDropFilter = useMemo(
    () => ({
      component: (
        <DropFilterContainer>
          <Dropdown
            onChange={(e) => selSelectedBType(e.target.value)}
            options={PROPERTY_TYPE_FILTER_OPTIONS}
            value={selectedBType}
            multiselect
            showSearch={false}
            placeholder="Property Type"
            label="Property Type"
            style={{ width: '100%' }}
          />
          <Dropdown
            onChange={(e) => setIsSHouse(e.target.value)}
            options={BOOLEAN_YES_NO_OPTIONS}
            value={isSHouse}
            multiselect
            showSearch={false}
            placeholder="Student Housing"
            label="Student Housing"
            style={{ width: '100%' }}
          />
        </DropFilterContainer>
      ),
      resetFunc: resetDelegatedFilters,
      applyFunc: applyDelegatedFilters,
    }),
    [selectedBType, isSHouse, resetDelegatedFilters, applyDelegatedFilters],
  );

  const myDropFilter = useMemo(
    () => ({
      component: (
        <DropFilterContainer>
          <Dropdown
            onChange={(e) => setMySelectedBType(e.target.value)}
            options={PROPERTY_TYPE_FILTER_OPTIONS}
            value={mySelectedBType}
            multiselect
            showSearch={false}
            placeholder="Property Type"
            label="Property Type"
            style={{ width: '100%' }}
          />
          <Dropdown
            onChange={(e) => setMyIsSHouse(e.target.value)}
            options={BOOLEAN_YES_NO_OPTIONS}
            value={myIsSHouse}
            multiselect
            showSearch={false}
            placeholder="Student Housing"
            label="Student Housing"
            style={{ width: '100%' }}
          />
        </DropFilterContainer>
      ),
      resetFunc: resetMyFilters,
      applyFunc: applyMyFilters,
    }),
    [mySelectedBType, myIsSHouse, resetMyFilters, applyMyFilters],
  );

  return (
    <DashboardLayoutContainer shouldShowCard={false}>
      {detailLoading || detailHasError ? (
        <DetailPageShell
          onBack={onClickBack}
          title={user ? maskDemoField(isDemo, 'username', user.accepter.username) : null}
          isLoading={detailLoading}
          hasError={detailHasError}
          onRetry={onRetry}
          loadingLabel="Loading user details..."
          skeletonVariant="fields"
        />
      ) : (
        <>
          {user && (
            <Box
              padding="24px"
              gap="10px"
              display="flex"
              flexDirection="column"
              border="1px solid #EAEAEA"
              sx={{ background: 'white' }}
              borderRadius="12px"
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <Box sx={{ cursor: 'pointer', '&:hover': { opacity: 0.75 } }}>
                    <IconGraphy icon="Arrow.ArrowBack" onClick={onClickBack} />
                  </Box>
                  <Typography
                    variant="h3"
                    style={{
                      color: colors.purple[900],
                      fontWeight: '500',
                      padding: '5px 5px 5px 15px',
                    }}
                  >
                    {maskDemoField(isDemo, 'username', user.accepter.username)}
                  </Typography>
                </Box>
                {disable && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                    }}
                  >
                    <Typography variant="body3" style={{ color: colors.neutral[700] }}>
                      {pendingAddedCount} to add · {pendingRemovedCount} to remove
                    </Typography>
                    <Button
                      size="medium"
                      variant="tertiary"
                      style={{ flexShrink: 2 }}
                      onClick={handleDiscard}
                    >
                      Discard
                    </Button>

                    <Button
                      size="medium"
                      variant="primary"
                      style={{ flexShrink: 2, whiteSpace: 'nowrap' }}
                      onClick={handleSave}
                      iconPrefix="General.SaveAs"
                    >
                      Save Changes
                    </Button>
                  </Box>
                )}
              </Box>
            </Box>
          )}

          {buildings ? (
            <PropertiesTableCard
              border="1px solid #EAEAEA"
              sx={{ background: 'white', marginTop: '8px' }}
              borderRadius="12px"
              padding="8px 16px 16px"
            >
              <Table
                ref={delegatedRef}
                title="Delegated Properties"
                columns={delegatedBuildingColumns}
                data={tableData}
                autoTooltipForDefaultCells
                filter={filter}
                onChangeFilter={setFilter}
                dropFilter={delegatedDropFilter}
                dropFilterActiveSummary={delegatedDropFilterActiveSummary}
                dropFilterActiveCount={delegatedDropFilterActiveCount}
                sortOptions={sortDelegatedBuildingOptions}
                onChangeSort={handleDelegatedSortChange}
                pagination={{
                  ...delegatedBuildingPagination,
                  totalItems: delegatedPropertiesTotal,
                  totalPages:
                    Math.ceil(delegatedPropertiesTotal / delegatedBuildingPagination.rowsPerPage) ||
                    0,
                }}
                onChangeRowsPerPage={(value) => {
                  setDelegatedBuildingPagination((prev) => ({
                    ...prev,
                    rowsPerPage: value,
                    pageNumber: 0,
                  }));
                }}
                showEmptyState={!buildingsJSON?.isLoading}
                emptyContent={
                  <GlobalUXState
                    variant={filter?.searchText ? 'noResults' : 'noData'}
                    compact
                    description={
                      filter?.searchText
                        ? 'No delegated properties match your search.'
                        : 'No delegated properties available yet.'
                    }
                  />
                }
                loading={shouldShowDelegatedLoading}
                mobileWidth={960}
                style={{ width: '100%' }}
              />
            </PropertiesTableCard>
          ) : (
            <Loading size="large" />
          )}

          {propertyBuildings && (
            <PropertiesTableCard
              border="1px solid #EAEAEA"
              sx={{ background: 'white', marginTop: '8px' }}
              borderRadius="12px"
              padding="8px 16px 16px"
            >
              <Table
                ref={myRef}
                title="My Properties"
                columns={myBuildingColumns}
                data={propertyTableData}
                autoTooltipForDefaultCells
                filter={myPropertiesFilter}
                onChangeFilter={setMyPropertiesFilter}
                dropFilter={myDropFilter}
                dropFilterActiveSummary={myDropFilterActiveSummary}
                dropFilterActiveCount={myDropFilterActiveCount}
                sortOptions={sortMyBuildingOptions}
                onChangeSort={handleMyPropertiesSortChange}
                pagination={{
                  ...myBuildingPagination,
                  totalItems: myPropertiesTotal,
                  totalPages: Math.ceil(myPropertiesTotal / myBuildingPagination.rowsPerPage) || 0,
                }}
                onChangeRowsPerPage={(value) => {
                  setMyBuildingPagination((prev) => ({
                    ...prev,
                    rowsPerPage: value,
                    pageNumber: 0,
                  }));
                }}
                showEmptyState={!propertyBuildings?.isLoading}
                emptyContent={
                  <GlobalUXState
                    variant={myPropertiesFilter?.searchText ? 'noResults' : 'noData'}
                    compact
                    description={
                      myPropertiesFilter?.searchText
                        ? 'No properties match your search.'
                        : 'No properties available to add yet.'
                    }
                  />
                }
                loading={Boolean(propertyBuildings?.isLoading) && propertyTableData.length > 0}
                mobileWidth={960}
                style={{ width: '100%' }}
              />
            </PropertiesTableCard>
          )}
        </>
      )}
    </DashboardLayoutContainer>
  );
};

const CustomTypography = styled(Typography)`
  word-break: break-all;
  overflow-wrap: anywhere;
`;

const DropFilterContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 12px;
  width: 100%;
  * {
    box-sizing: border-box;
  }
`;

/** ui-kit Dropdown always opens below (`top: calc(100% + 10px)`). On stacked
 * property tables that covers the section below and clips at the
 * page bottom (LLP-173). Flip only the footer Rows Per Page menu upward. */
const PropertiesTableCard = styled(Box)`
  overflow: visible;
  position: relative;

  /* Table root → footer (last child) → DropdownInput → menu after value trigger */
  & > div > div:last-child div:has(> .ui-kit-dropdown-value) + div {
    top: auto !important;
    bottom: calc(100% + 10px) !important;
    z-index: 300;
  }
`;
